/**
 * 分镜大图看图层 —— 2026-08-06 从 `VideoStoryboardGrid` 抽出成独立件。
 *
 * 抽出的理由不是拆分本身:老网格要退役,而这一层里躺着一条**用户实报事故的修复**,
 * 它必须和判据一起原样活下来,不能跟着宿主一起被删。
 *
 * ═══ 它修的那件事(2026-08-05 用户报障)═══════════════════════════════════════
 * 用户原话:「分镜那里替换图片,点开展示,没有明显地方提示关闭,导致只能网页退出,草稿都没了」。
 *
 * 真因(本地实拍 + 计算样式):三套皮肤都有
 *   `[data-skin="x"] [role="dialog"] { background: hsl(var(--popover)) !important }`
 * 它把这个**手写**的全屏看图层刷成了 popover 白底,而层里的关闭键当时是 `bg-white/10`
 * + 白色图标 —— **白底白键,完全隐形**。元素在 DOM 里、点得到,但用户看不见,只能关掉网页。
 *
 * 所以这里有两条不许动的:
 * 1. 根节点带 `data-video-lightbox` —— index.css 那条皮肤例外靠它生效,删了 bug 立刻复发;
 * 2. 关闭键**自带对比度**(不透明底 + 边框 + 阴影 + 可读文字),不依赖背板颜色。
 *    判据是「用户一眼能找到出口」,不是「元素在 DOM 里」。
 *
 * ★portal 到 body 全屏渲染:`fixed` 定位会被 transform 祖先劫持成相对该祖先定位
 *   (BP-01 同族病,实测弹窗贴左下角);portal 后 fixed 回到视口坐标系。
 * ★←/→ 跨镜翻看:对比连镜一致性正是看大图的主要动机。
 */
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

import type { PanelShotData } from "../../model/panelShot";

/**
 * 这一镜「显示的那张图」:首帧优先,否则台本预览图。
 * ★只认 firstFrameUrl 的话,T2V 镜明明有预览图却点不开大图(测试当场抓到)。
 * 与矮卡/检视面板同一口径,别各写一份。
 */
export const shownImageOf = (s: PanelShotData) => s.firstFrameUrl || s.previewUrl;

interface Props {
  shots: PanelShotData[];
  /** 当前看的是第几镜;null = 不显示。 */
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}

export function ShotLightbox({ shots, index, onClose, onIndexChange }: Props) {
  const previewables = shots.map((s, i) => (shownImageOf(s) ? i : -1)).filter((i) => i >= 0);

  const step = useCallback(
    (dir: 1 | -1) => {
      if (index === null || previewables.length === 0) return;
      const pos = previewables.indexOf(index);
      onIndexChange(previewables[(pos + dir + previewables.length) % previewables.length]);
    },
    [index, previewables, onIndexChange]
  );

  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, step, onClose]);

  const shot = index !== null ? shots[index] : null;
  const url = shot ? shownImageOf(shot) : undefined;
  if (!shot || !url || index === null) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`镜 ${index + 1} 大图`}
      // ★皮肤例外钩子(别删):没有它,三套皮肤的 [role=dialog] 规则会把黑背板刷成白色。
      data-video-lightbox
      className="fixed inset-0 z-50 flex flex-col bg-black/85"
    >
      {/* 点空白处关闭:背板做成真按钮(而不是给 div 挂 onClick)—— 键盘/读屏可达 */}
      <button
        type="button"
        aria-label="关闭预览"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div className="relative flex justify-end p-3">
        {/* ★关闭键必须自带对比度,不许依赖背板颜色:半透明底压在深色画面上等于隐形,
            白键压在白背板上同样等于隐形(用户报障的实拍)。 */}
        <button
          type="button"
          aria-label="关闭预览"
          onClick={onClose}
          className="flex h-9 cursor-pointer items-center gap-1 rounded-full border border-border bg-background px-3 text-sm text-foreground shadow-lg transition-colors hover:bg-accent"
        >
          <X className="h-4 w-4" />
          关闭
        </button>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center gap-3 px-4">
        {previewables.length > 1 && (
          <button
            type="button"
            aria-label="上一镜首帧"
            onClick={() => step(-1)}
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img
          src={url}
          alt={`镜 ${index + 1} 大图`}
          className="max-h-full min-h-0 w-auto max-w-[calc(100%-7rem)] rounded-lg object-contain"
        />
        {previewables.length > 1 && (
          <button
            type="button"
            aria-label="下一镜首帧"
            onClick={() => step(1)}
            className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>
      <div className="relative px-6 py-3 text-center text-xs text-white/80">
        镜 {index + 1} / {shots.length}
        {[shot.scene, shot.motion].filter((s) => s?.trim()).length > 0 &&
          ` · ${[shot.scene, shot.motion]
            .map((s) => (s ?? "").trim())
            .filter(Boolean)
            .join(" · ")}`}
      </div>
    </div>,
    document.body
  );
}
